'use client';
import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { EventResponse } from '@/types';
import { ResolvedTradeView } from '../markets/TradeModal';
import { SportsGameCard } from './SportsGameCard';

interface TradeSidebarProps {
  selectedEvent: EventResponse | null;
  onClose?: () => void;
}

export const TradeSidebar = ({ selectedEvent, onClose }: TradeSidebarProps) => {
  const { t } = useT();
  const winnerName = useMemo(() => {
    if (!selectedEvent) return t('trade.draw');
    const h = parseFloat(selectedEvent.homeTeam?.score || '0');
    const a = parseFloat(selectedEvent.awayTeam?.score || '0');
    if (h > a) return selectedEvent.homeTeam?.name || 'Home';
    if (a > h) return selectedEvent.awayTeam?.name || 'Away';
    return t('trade.draw');
  }, [selectedEvent, t]);

  if (!selectedEvent) {
    return null;
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: 320 },
        flexShrink: 0,
        borderLeft: { xs: 'none', lg: '1px solid' },
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        height: { xs: 'auto', lg: 'calc(100vh - 100px)' },
        position: 'sticky',
        top: 20,
        overflowY: 'auto',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('trade.trade')}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <SportsGameCard
          event={selectedEvent}
          onTeamClick={() => {}} // No-op here maybe? Or trigger trade modal?
        />

        {selectedEvent.ended ||
        (selectedEvent.isLive === false &&
          (selectedEvent.homeTeam?.score || selectedEvent.awayTeam?.score)) ? (
          <ResolvedTradeView event={selectedEvent} winnerName={winnerName} />
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {t('trade.orderBook')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trade.selectOutcome')}
            </Typography>
            {/* Add dummy order book or trade controls here if needed */}
          </Box>
        )}
      </Box>
    </Box>
  );
};
